import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  BarChart3,
  Building2,
  Calendar,
  CheckCircle,
  ClipboardList,
  Compass,
  Eye,
  FileText,
  FolderOpen,
  HelpCircle,
  Layers,
  LogOut,
  Mail,
  ScrollText,
  Settings,
  Shield,
  User,
  UserCheck,
  Users,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import NavNewBadge from './NavNewBadge';

const ICON_MAP = {
  BarChart3,
  Building2,
  Calendar,
  CheckCircle,
  ClipboardList,
  Compass,
  Eye,
  FileText,
  FolderOpen,
  HelpCircle,
  Layers,
  LogOut,
  Mail,
  ScrollText,
  Settings,
  Shield,
  User,
  UserCheck,
  Users,
};

/**
 * @param {Object} props
 * @param {import('../../navigation/buildNavSections').ResolvedNavItem} props.item
 * @param {boolean} props.isActive
 * @param {boolean} props.collapsed
 * @param {(item: import('../../navigation/buildNavSections').ResolvedNavItem) => void} props.onSelect
 */
export default function SidebarNavItem({ item, isActive, collapsed, onSelect, pending = false }) {
  const Icon = ICON_MAP[item.icon] || Layers;
  const showDiscovery = item.highlightDiscovery && !isActive;
  const isLink = item.type === 'link' && item.path;

  const className = cn(
    'relative flex w-full items-center gap-2 rounded-lg text-left transition-colors min-h-[44px] max-md:gap-1.5 lg:min-h-8',
    collapsed ? 'justify-center px-1.5 py-2 max-md:px-1 max-md:py-1.5 lg:px-2 lg:py-1.5' : 'px-2 py-2 max-md:px-1.5 max-md:py-1.5 md:px-2 md:py-1.5 lg:py-1.5',
    isActive
      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
      : showDiscovery
        ? 'bg-indigo-50/90 text-indigo-900 ring-1 ring-indigo-300/80 hover:bg-indigo-100 dark:bg-indigo-950/30 dark:text-indigo-100 dark:ring-indigo-700/60 dark:hover:bg-indigo-900/40'
        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
  );

  const content = (
    <>
      <span className="relative flex-shrink-0">
        {pending ? (
          <Loader2 className="h-5 w-5 lg:h-4 lg:w-4 animate-spin" aria-hidden="true" />
        ) : (
          <Icon className={cn('h-5 w-5 lg:h-4 lg:w-4', item.iconColor)} aria-hidden="true" />
        )}
        {collapsed && item.showNewBadge ? (
          <span
            className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-gray-900"
            aria-hidden="true"
          />
        ) : null}
      </span>
      {!collapsed ? (
        <>
          <span className="min-w-0 flex-1 truncate text-ds-body font-medium">
            {pending ? 'Signing out…' : item.label}
          </span>
          {item.showNewBadge ? <NavNewBadge className="ml-auto" /> : null}
        </>
      ) : null}
    </>
  );

  const walkthroughId = `nav-${item.id}`;

  const navItem = isLink ? (
    <Link
      to={item.path}
      onClick={() => onSelect(item)}
      className={className}
      data-walkthrough={walkthroughId}
      aria-current={isActive ? 'page' : undefined}
      aria-label={collapsed ? item.label : undefined}
    >
      {content}
    </Link>
  ) : (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className={className}
      data-walkthrough={walkthroughId}
      disabled={pending}
      aria-busy={pending || undefined}
      aria-current={isActive ? 'page' : undefined}
      aria-label={collapsed ? item.label : undefined}
    >
      {content}
    </button>
  );

  if (!collapsed) return navItem;

  const tooltipLabel = item.showNewBadge ? `${item.label} · NEW` : item.label;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{navItem}</TooltipTrigger>
      <TooltipContent
        side="right"
        sideOffset={8}
        collisionPadding={16}
        className="z-[150] max-w-[min(16rem,calc(100vw-var(--sidebar-w-collapsed,64px)-2rem))] dark:bg-gray-800 dark:border-gray-700"
      >
        <span className="inline-flex items-center gap-1.5">
          {tooltipLabel}
          {item.showNewBadge ? <NavNewBadge compact /> : null}
        </span>
      </TooltipContent>
    </Tooltip>
  );
}
